/**
 * app/deeplink.ts — a launch from a link opens what the link names.
 *
 * Task 15. `spec.md` «Бот» puts a direct link `t.me/<бот>/<приложение>?startapp=…` under every
 * track the bot sends and on the menu button. The checklist: «Глубокая ссылка `startapp=home`
 * открывает главный экран; ссылка на трек открывает трек в плеере; неизвестный параметр — главный
 * экран без ошибки».
 *
 * ## The payload is read from the session, never from the address bar
 *
 * `store/session.ts` holds the copy the server read out of the launch it has just verified
 * (`apps/api/src/routes/auth.ts`, `startParam`). The address bar's `tgWebAppStartParam` is the
 * same text unsigned, and anybody can type anything there.
 *
 * ## «Opens the track in the player» — and does not start it
 *
 * The track becomes the whole queue and is held at its first second, the way a restored launch
 * holds its track (`engine/player.ts`, `cue`); the shell then opens «Сейчас играет» over it. It
 * does not play: the tap that opened the app happened in Telegram's chat, not in this page, so
 * the phone would refuse the sound (`spec.md` «Плеер»: «нет попытки автозапуска до жеста»). The
 * record waits under the listener's thumb, and the first tap plays it.
 *
 * The queue is replaced, not added to: a tapped link is as plain a request for one track as a
 * tapped row, and a tapped row replaces the queue (`engine/player.ts`, `playFrom`). The price is
 * stated: the queue left by the last launch is not held in the mini player, and is overwritten
 * on the server by the next save.
 *
 * ## Every failure is the home screen
 *
 * A payload that names no track never gets here — the shell reads it and goes on. A track that
 * has left the channel, no network: the answer is `false`, and the shell goes on as for any
 * launch. So is a listener who chose something while
 * the request was out — their choice is newer than the link.
 *
 * ## A clip opens in «Видео», shown and not started (task 16)
 *
 * A line of the weekly digest names a clip by `startapp=v<id>`. The clip becomes the one in the
 * tab's frame — `selected` of the video slice, which is what the tab shows first
 * (`screens/Video.tsx`) — and the shell opens the tab. Not started, for the reason a track is not:
 * the tap was in Telegram. The music queue is not touched — a clip is never a track — so the
 * saved queue is read and held in the mini player as on any launch.
 */

import { player } from '../engine/player';
import { fetchTrack, fetchVideo } from '../lib/api';
import { usePlayer } from '../store/player';
import { useQueue } from '../store/queue';
import { useSession } from '../store/session';
import { isPlayable, useVideo } from '../store/video';

/**
 * Opens track `trackId` — the one `parseStartParam` (`@fdm/shared`) read out of the launch.
 * `true` when it was opened; `false` for everything else — see «Every failure».
 */
export async function openStartTrack(trackId: number): Promise<boolean> {
  const revision = useQueue.getState().revision;
  const result = await fetchTrack(useSession.getState().session?.token, trackId);
  if (!result.ok) return false;
  if (useQueue.getState().revision !== revision || usePlayer.getState().track !== undefined) {
    return false;
  }

  useQueue.getState().replace([result.data]);
  player().cue(0);
  return true;
}

/**
 * Puts clip `videoId` in the frame of «Видео», for the shell to open the tab on. `true` when it
 * was put there; `false` for a clip that is gone or cannot be played, for no network, and when a
 * clip was chosen while the request was out.
 */
export async function openStartVideo(videoId: number): Promise<boolean> {
  const result = await fetchVideo(useSession.getState().session?.token, videoId);
  if (!result.ok || !isPlayable(result.data)) return false;
  if (useVideo.getState().selected !== undefined) return false;
  useVideo.setState({ selected: result.data });
  return true;
}
